import { Button, ContainerNoTopSpace, ContainerWebinarInner, ErrorMessagePage, Heading } from '@core/ui';
import { BackLink } from '@online-webinar/ui-frontend';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LegendItem } from './charts/TimeChart';
import { LoadChoice } from './LoadChoice';
import { MAX_SECONDS } from './perSecond';
import { SummaryTable } from './SummaryTable';
import { TargetPanel } from './TargetPanel';
import { useLineQuality } from './useLineQuality';

/** The live view shows the last ten minutes; the whole run is compressed into about as many slots. */
const LIVE_SECONDS = 600;

/**
 * The line test of the test suite (spec 7): measures the line towards our probe servers, with an
 * optional upload load, live per-second charts per target and the reported windows below. Asks for
 * no device, so it also runs on an encoder PC without a camera.
 */
export const LineQualityPage = () => {
    const { t } = useTranslation();
    const { phase, error, dismissError, level, setLevel, run, now, start, stop } = useLineQuality();
    const [wholeRun, setWholeRun] = useState(false);

    const backLink = <BackLink to="/test-suite">{t('TestSuite.Line.BackToChoice')}</BackLink>;

    const measuring = phase === 'starting' || phase === 'running';
    const series = run?.series;
    const through = series ? (measuring ? series.settledThrough(now) : series.lastSecond()) : -1;
    const to = Math.max(0, through);
    const from = wholeRun ? Math.max(0, to - MAX_SECONDS + 1) : Math.max(0, to - LIVE_SECONDS + 1);
    const slot = wholeRun ? Math.max(1, Math.ceil((to - from + 1) / LIVE_SECONDS)) : 1;
    const levelMarks = run?.levelMarks;
    const marks = useMemo(() => levelMarks?.map((m) => m.second) ?? [], [levelMarks]);
    const axis = useMemo(() => ({ from, to, slot, marks }), [from, to, slot, marks]);
    const targets = run?.targets;
    const windows = run?.windows;
    const windowsByTarget = useMemo(() => new Map(targets?.map((target) => [target, windows?.filter((w) => w.target === target) ?? []])), [targets, windows]);
    // Freeze side effect: seconds() releases the raw samples of every second up to `through` once it
    // has summarised them (their percentiles stay), so a two-hour run does not keep every round trip.
    // Pass only settled seconds here. Recomputed on the one-second tick (`now`), not on every status or
    // window update.
    const bucketsByTarget = useMemo(
        () => new Map(targets?.map((target) => [target, series?.seconds(target, through) ?? []])),
        // The series mutates in place; `now` (the one-second tick) stands for its version.
        [series, targets, through, now]
    );
    // As above: the series mutates in place, `now` stands for its version.
    const stalledByTarget = useMemo(() => new Map(targets?.map((target) => [target, series?.stalledSpans(target) ?? []])), [series, targets, now]);

    if (error) {
        return (
            <ErrorMessagePage apiError={error} errorSubject="TestSuite.Line.ApiErrorCode">
                <div className="flex flex-wrap items-center gap-4">
                    <Button variant="primary" onClick={dismissError}>
                        {t('TestSuite.Line.Button.Retry')}
                    </Button>
                    {backLink}
                </div>
            </ErrorMessagePage>
        );
    }

    return (
        <ContainerNoTopSpace>
            <ContainerWebinarInner>
                <div className="mb-2">{backLink}</div>
                <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                    {t('TestSuite.Line.Heading')}
                </Heading>
                <div className="bg-white rounded-lg max-w-480 p-4 md:p-6 z-1">
                    <p className="mb-2 max-w-3xl">{t('TestSuite.Line.Intro')}</p>
                    <p className="mb-6 max-w-3xl font-semibold">{t('TestSuite.Line.Advice')}</p>
                    <div className="flex flex-col md:flex-row gap-6 md:items-end md:justify-between">
                        <LoadChoice level={level} onChange={setLevel} status={run?.loadStatus ?? { state: 'off' }} />
                        <Button variant="primary" classNames="self-start md:self-end" onClick={measuring ? stop : start} isLoading={phase === 'starting'}>
                            {measuring ? t('TestSuite.Line.Button.Stop') : t('TestSuite.Line.Button.Start')}
                        </Button>
                    </div>
                </div>
                {run && series && run.targets.length > 0 && (
                    <>
                        <div className="bg-white rounded-lg max-w-480 p-4 md:p-6 mt-4">
                            <div className="flex flex-wrap items-center justify-between gap-4 mb-4 text-sm">
                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={wholeRun} onChange={(e) => setWholeRun(e.target.checked)} />
                                    {t('TestSuite.Line.Chart.WholeRun')}
                                </label>
                                <span className="flex flex-wrap gap-x-4 text-xs">
                                    <LegendItem swatch="bg-gray-200" label={t('TestSuite.Line.Legend.Unmeasured')} />
                                    <LegendItem swatch="bg-warning-light" label={t('TestSuite.Line.Legend.Stalled')} />
                                    <LegendItem swatch="bg-accent" label={t('TestSuite.Line.Legend.Background')} />
                                    <LegendItem swatch="border-l-2 border-dashed border-gray-500 rounded-none" label={t('TestSuite.Line.Legend.LoadChange')} />
                                </span>
                            </div>
                            {through < 0 && <p className="mb-4">{t('TestSuite.Line.Collecting')}</p>}
                            {run.targets.map((target, i) => (
                                <TargetPanel
                                    key={target}
                                    target={target}
                                    status={run.statuses[target]}
                                    buckets={bucketsByTarget.get(target) ?? []}
                                    stalled={stalledByTarget.get(target) ?? []}
                                    windows={windowsByTarget.get(target) ?? []}
                                    axis={axis}
                                    through={through}
                                    originWall={series.originWall}
                                    loadSteps={i === 0 ? run.loadSteps : undefined}
                                />
                            ))}
                        </div>
                        <div className="bg-white rounded-lg max-w-480 p-4 md:p-6 mt-4">
                            <SummaryTable targets={run.targets} windows={run.windows} />
                        </div>
                    </>
                )}
            </ContainerWebinarInner>
        </ContainerNoTopSpace>
    );
};
